import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Autocomplete, Chip, FormControl, Grid, MenuItem, Stack, TextField } from "@mui/material";
import { CaretDownIcon } from "@phosphor-icons/react/dist/ssr/CaretDown";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import type { RequestObjective } from "@/hooks/api/use-campaign-requests";
import { useUpdateCampaignRequest } from "@/hooks/api/use-campaign-requests";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

import { LANGUAGES, MARKETS, REQUEST_OBJECTIVE_OPTIONS } from "../constants";
import type { StepProps } from "./wizard-shell";
import { StepHeading, useWizardDirty, WizardFooter } from "./wizard-shell";

/**
 * Step 1 — campaign basics.
 *
 * Two things earn their place here that a form would otherwise leave out. The project reference is
 * labelled with *why* it exists — it flows onto the invoice and the statement, which is what lets
 * a finance team reconcile without asking us. And markets and languages are separate fields that
 * do not have to line up one-to-one: Singapore and Australia share one English agent. Step 3 is
 * where the pairing actually happens.
 */

const schema = z.object({
	title: z.string().trim().min(1, "Give this campaign a name"),
	clientReference: z.string(),
	objective: z.string(),
	markets: z.array(z.string()).min(1, "Choose at least one market"),
	languages: z.array(z.string()).min(1, "Choose at least one language"),
	notes: z.string(),
});

type FormValues = z.infer<typeof schema>;

export function StepBasics({ request, goTo, exit, readOnly }: StepProps): React.JSX.Element {
	const updateMutation = useUpdateCampaignRequest();

	const {
		control,
		handleSubmit,
		getValues,
		formState: { errors, isDirty },
	} = useForm<FormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			// "Untitled campaign" is the placeholder the draft was created with, not a name the client
			// chose, so it is cleared rather than presented back to them as their own answer.
			title: request.title === "Untitled campaign" ? "" : request.title,
			clientReference: request.clientReference ?? "",
			objective: request.objective ?? "",
			markets: request.markets ?? [],
			languages: request.languages ?? [],
			notes: request.notes ?? "",
		},
	});

	// The rail is the one way off this step that writes nothing, so it has to ask first.
	useWizardDirty(isDirty);

	const save = async (values: FormValues): Promise<boolean> => {
		// Nothing moved, so nothing is written. A no-op PUT still runs `recompute()` on the API,
		// which withdraws a live quote and sends the request back for re-pricing — see `./dirty`.
		if (!isDirty) return true;

		try {
			await updateMutation.mutateAsync({
				id: request.id,
				data: {
					title: values.title.trim() || request.title || "Untitled campaign",
					clientReference: values.clientReference.trim() || null,
					objective: (values.objective || null) as RequestObjective | null,
					markets: values.markets,
					languages: values.languages,
					notes: values.notes.trim() || null,
				},
			});
			return true;
		} catch {
			// The hook toasts the API's message — a frozen request names the state that blocked it.
			return false;
		}
	};

	const onContinue = handleSubmit(async (values) => {
		if (await save(values)) goTo(2);
	});

	const onSaveExit = async () => {
		if (await save(getValues())) exit();
	};

	return (
		<Stack spacing={3}>
			<StepHeading title="Tell us about this campaign" help="The heading, the reference and who it is aimed at." />

			<Grid container spacing={2}>
				<Grid size={{ xs: 12, md: 6 }}>
					<Controller
						control={control}
						name="title"
						render={({ field }) => (
							<FormControl fullWidth error={Boolean(errors.title)}>
								<TextField
									{...field}
									required
									disabled={readOnly}
									label="Campaign name"
									error={Boolean(errors.title)}
								/>
								{errors.title ? <CustomFormHelperError text={errors.title.message ?? ""} /> : null}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={{ xs: 12, md: 6 }}>
					<Controller
						control={control}
						name="clientReference"
						render={({ field }) => (
							<FormControl fullWidth>
								{/* Labelled with why it is asked for: it is what makes an invoice reconcilable. */}
								<TextField
									{...field}
									disabled={readOnly}
									label="Your project reference"
									helperText="Appears on the invoice and the statement."
								/>
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={{ xs: 12, md: 6 }}>
					<Controller
						control={control}
						name="objective"
						render={({ field }) => (
							<FormControl fullWidth>
								<TextField
									{...field}
									select
									disabled={readOnly}
									label="What is this campaign for?"
									helperText="Pre-fills your outcomes."
								>
									{REQUEST_OBJECTIVE_OPTIONS.map((option) => (
										<MenuItem key={option.value} value={option.value}>
											{option.label}
										</MenuItem>
									))}
								</TextField>
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={{ xs: 12, md: 6 }}>
					<Controller
						control={control}
						name="markets"
						render={({ field }) => (
							<FormControl fullWidth error={Boolean(errors.markets)}>
								<Autocomplete
									multiple
									disableCloseOnSelect
									disabled={readOnly}
									options={MARKETS}
									popupIcon={<CaretDownIcon size={18} />}
									getOptionLabel={(option) => `${option.flag} ${option.label}`}
									value={MARKETS.filter((market) => field.value.includes(market.value))}
									onChange={(_event, selected) => field.onChange(selected.map((market) => market.value))}
									renderValue={(selected, getItemProps) =>
										selected.map((market, index) => {
											// `key` comes back inside the item props; spreading it into JSX is a React warning.
											const { key, ...itemProps } = getItemProps({ index });
											return <Chip {...itemProps} key={key} label={`${market.flag} ${market.label}`} size="small" />;
										})
									}
									renderInput={(params) => (
										<TextField {...params} required label="Markets" error={Boolean(errors.markets)} />
									)}
								/>
								{errors.markets ? <CustomFormHelperError text={errors.markets.message ?? ""} /> : null}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={{ xs: 12, md: 6 }}>
					<Controller
						control={control}
						name="languages"
						render={({ field }) => (
							<FormControl fullWidth error={Boolean(errors.languages)}>
								<Autocomplete
									multiple
									disableCloseOnSelect
									disabled={readOnly}
									options={LANGUAGES}
									popupIcon={<CaretDownIcon size={18} />}
									getOptionLabel={(option) => option.label}
									value={LANGUAGES.filter((language) => field.value.includes(language.value))}
									onChange={(_event, selected) => field.onChange(selected.map((language) => language.value))}
									renderValue={(selected, getItemProps) =>
										selected.map((language, index) => {
											const { key, ...itemProps } = getItemProps({ index });
											return <Chip {...itemProps} key={key} label={language.label} size="small" />;
										})
									}
									renderInput={(params) => (
										<TextField
											{...params}
											required
											label="Languages"
											error={Boolean(errors.languages)}
											// Named an Australia the client may never have chosen. One per language,
											// not one per market, is the whole of the point.
											helperText="One per language, not one per market."
										/>
									)}
								/>
								{errors.languages ? <CustomFormHelperError text={errors.languages.message ?? ""} /> : null}
							</FormControl>
						)}
					/>
				</Grid>

				<Grid size={12}>
					<Controller
						control={control}
						name="notes"
						render={({ field }) => (
							<FormControl fullWidth>
								<TextField
									{...field}
									multiline
									disabled={readOnly}
									minRows={3}
									label="Anything we should know?"
									placeholder="Context, constraints, who to contact…"
									helperText="Optional."
								/>
							</FormControl>
						)}
					/>
				</Grid>
			</Grid>

			<WizardFooter
				busy={updateMutation.isPending}
				onBack={() => goTo(0)}
				onSaveExit={readOnly ? undefined : () => void onSaveExit()}
				onContinue={readOnly ? () => goTo(2) : () => void onContinue()}
			/>
		</Stack>
	);
}
